import type { EvidenceHighlight } from "@/components/ui/document-viewer";
import type { IndicationVerdictView } from "./useIndicationVerdicts";

export interface FocusedMention {
  position: number;
  mentionIndex: number;
}

export const mentionKeyOf = (position: number, mentionIndex: number) => `${position}:${mentionIndex}`;

const TITLE_MAX_LENGTH = 80;

/** The stored name, or the first quote for rows written before names were stored. */
export const titleOf = (verdict: Pick<IndicationVerdictView, "label" | "mentions">) => {
  const name = verdict.label?.trim();
  if (name) return name;

  const text = verdict.mentions.find((mention) => mention.text.trim() !== "")?.text.trim();
  if (!text) return "Indication";

  return text.length > TITLE_MAX_LENGTH ? `${text.slice(0, TITLE_MAX_LENGTH).trimEnd()}…` : text;
};

/** One highlight per mention that sits on a document tab and has text to find. */
export function buildIndicationHighlights(
  verdicts: readonly IndicationVerdictView[],
  focused: FocusedMention | null,
): EvidenceHighlight[] {
  return verdicts.flatMap((verdict) => {
    const title = titleOf(verdict);

    return verdict.mentions.flatMap((mention, mentionIndex) => {
      if (mention.fileIdentifier === null || mention.text.trim() === "") return [];

      return [
        {
          text: mention.text,
          code: `indication::${mentionKeyOf(verdict.position, mentionIndex)}`,
          isFocused: false,
          isActiveQuote: focused?.position === verdict.position && focused.mentionIndex === mentionIndex,
          fileIdentifier: mention.fileIdentifier,
          indication: {
            key: mentionKeyOf(verdict.position, mentionIndex),
            title,
            verdict: verdict.verdict,
            isNotClassified: verdict.isNotClassified,
            definition: verdict.ruleDefinition,
            reason: verdict.reason,
            sourceLabel: mention.source,
            personalHistory: verdict.personalHistory,
            absent: verdict.absent,
          },
        },
      ];
    });
  });
}

/** A failed check is still to code, and says so. */
export const verdictTextOf = (verdict: Pick<IndicationVerdictView, "verdict" | "isNotClassified">) => {
  const outcome = verdict.verdict === "coded" ? "To code" : "Not to code";

  return verdict.isNotClassified ? `${outcome} · not classified` : outcome;
};
